/* eslint-disable no-nested-ternary */
import AppDispacher from "./actions";
import MetaConfigTags from "./components/MetaConfig";
import Scripts from "./components/Scripts";
import ChinaScripts from "./components/ChinaScripts";
import BlogScripts from "./components/BlogScripts";
import LoginWrapper from "@Components/Login";
import ToastNotification from "@Components/ToastNotification";
import UcasAnnouncementModal from "@Components/UcasAnnouncementModal";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import getVisitorId from "helpers/getVisitorId";
import isInternalUser from "@Utils/isInternalUser";
import { AppState } from "@Store/index";
import config from "@Config/index";
import useWhitelabel from "hooks/useWhitelabel";
import WhitelabelScripts from "./components/WhitelabelScripts";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import MiniProgramScripts from "./components/MiniProgramScripts";
import AppScripts from "./components/Scripts/AppScripts";
import useIsApp from "hooks/useIsApp";
import { publishWebState } from "@Utils/webApputils/nativeStateBridge";
import {
  registerPostHogSuperProperties,
  setDestinationCountryForPostHog,
  setHasShortlistedForPostHog,
  setUserIdForPostHog,
} from "@Utils/posthog";
import IframeEventListener from "./components/IframeEventListener";
import LanguageSelectorModalSSR from "@Components/LanguageSelectorModal/LanguageSelectorModalSSR";

const GlobalComponentsWrapper = ({ growthbook }: any) => {
  const dispatch = useDispatch();
  const {
    userData,
    locality,
    isDesktop,
    countryCode,
    regionId,
    nationality,
    providerId,
    country,
    inventoryId,
    nonce,
    moduleName,
    userLanguage,
    metaObj,
  } = useSelector(({ appData }: AppState) => appData);

  const { isWhitelabel } = useWhitelabel();
  const isMiniProgram = useIsMiniProgram();
  const isApp = useIsApp();

  // Check if current page is a content page (blog/news) based on moduleName
  const isContentPage = useMemo(
    () => moduleName?.startsWith("blog_") || moduleName?.startsWith("news_") || false,
    [moduleName],
  );

  // Publish web-owned state to the native shell once per page load. The per-mutation calls are
  // a liveness optimisation; this one is what makes the contract correct — it also backfills
  // users whose history exists only in localStorage, so no migration flag is needed. No-op
  // outside the iOS shell.
  useEffect(() => {
    publishWebState();
  }, []);

  useEffect(() => {
    if (!config.IS_CHINA_APP) {
      growthbook.init();
    }
    const appDispatcher = new AppDispacher(dispatch);
    appDispatcher.fetchCountryData();
    appDispatcher.fetchGlobalTrustPilotRating();
  }, []);

  // The list key is absent, not `[]`, when nothing is shortlisted, so
  // `?.length` alone made an empty shortlist look signed-out and `false` was
  // never written. Keyed on the profile id instead.
  const shortlistedCount: number | undefined = userData?.data?.id
    ? userData.data.inventories_shortlists_ids?.length ?? 0
    : undefined;
  const profileUserId = userData?.data?.id;

  useEffect(() => {
    if (!countryCode) return;
    setDestinationCountryForPostHog(countryCode);
    registerPostHogSuperProperties().catch(() => undefined);
  }, [countryCode]);

  // Derived from the shortlist itself rather than written by the shortlist
  // button: `ShortListButton` already dispatches the new list on add and on
  // remove, so reading it here covers all three of its handlers, and the user's
  // existing shortlist on first load. `undefined` means signed out or not
  // loaded yet — that is unknown, not "no shortlist", so nothing is written.
  useEffect(() => {
    if (shortlistedCount === undefined) return;
    setHasShortlistedForPostHog(shortlistedCount > 0);
    registerPostHogSuperProperties().catch(() => undefined);
  }, [shortlistedCount]);

  // Every logged-in page load, not just the moment of logging in — which is
  // where it used to be set, and why coverage was so low.
  useEffect(() => {
    if (!profileUserId) return;
    setUserIdForPostHog(profileUserId).catch(() => undefined);
  }, [profileUserId]);

  useEffect(() => {
    if (!config.IS_CHINA_APP) {
      growthbook.setAttributes({
        id: getVisitorId(),
        internal_user: isInternalUser(userData),
        ...(userData?.data?.email ? { email: userData?.data?.email } : {}),
        ...(locality ? { locality } : {}),
        ...(country ? { country } : {}),
        ...(countryCode ? { countryCode } : {}),
        ...(regionId ? { regionId } : {}),
        ...(providerId ? { providerId } : {}),
        ...(nationality ? { nationality } : {}),
        ...(inventoryId ? { inventoryId } : {}),
        deviceType: isDesktop ? "desktop" : "mobile",
        isWhitelabel: isWhitelabel ? "true" : "false",
      });
    }
  }, [
    locality,
    countryCode,
    isWhitelabel,
    country,
    JSON.stringify(userData),
    regionId,
    providerId,
    nationality,
  ]);

  const getScriptComponent = () => {
    if (__CLIENT__ || __DEV__) return null;

    switch (true) {
      case isApp:
        return <AppScripts nonce={nonce} />;
      case isMiniProgram:
        return <MiniProgramScripts />;
      case config.IS_CHINA_APP:
        return <ChinaScripts nonce={nonce} />;
      case config.IS_WHITELABEL_APP:
        return <WhitelabelScripts nonce={nonce} />;
      case isContentPage:
        return <BlogScripts nonce={nonce} />;
      default:
        return <Scripts nonce={nonce} />;
    }
  };

  return (
    <>
      <LoginWrapper />
      <MetaConfigTags />
      {!config.IS_CHINA_APP && !isWhitelabel && (
        <LanguageSelectorModalSSR
          userLanguage={userLanguage}
          canonicalUrl={metaObj?.canonical_url_suffix}
          excludeAlternateLinks={metaObj?.excludeAlternateLinks}
        />
      )}
      <ToastNotification />
      <UcasAnnouncementModal />
      {config.IS_WHITELABEL_APP && <IframeEventListener />}
      {getScriptComponent()}
    </>
  );
};

export default GlobalComponentsWrapper;
